/**
 * Financial sensitivity (FA pack — FA6).
 *
 * Pure tornado-chart driver. For each "sensitive" variable on a
 * FinancialAssumptionsCore, compute the recomputed LCOE / IRR / NPV
 * at ±deltaPct of the base value (default ±20%) and return the swing.
 *
 * Output is sorted by the absolute swing magnitude of the active
 * metric so the UI can render a tornado strip (largest swing at top).
 *
 * Pure: no UI, no store. Mirrors `recompute()` discipline — no
 * mutation, every result derived from a shallow clone of the base
 * core.
 *
 * Unit discipline (same as projectFinance.ts):
 *   - capacityMW         ⇒ MW
 *   - capacityFactorPct  ⇒ percent (NOT fraction)
 *   - capexTotalUsd      ⇒ USD (NOT $k / $M)
 *   - fixedOmPerKwYearUsd ⇒ $/kW/yr
 *   - variableOmPerMwhUsd / fuelCostPerMwhUsd / ppaPricePerMwhUsd ⇒ $/MWh
 *   - waccPct / *Pct     ⇒ percent
 *   - Output LCOE        ⇒ $/MWh
 *
 * PHASED PATH (B10). `computePhasedSensitivity` measures the CANONICAL
 * scenario (core + phases), prices an unpriced phase at its capacity share of
 * the campus CAPEX rather than at the whole campus total, and rolls the phases
 * up per metric: LCOE energy-weighted, NPV summed, IRR solved on the aggregate
 * cashflow vector by `projectFinancePhased` — never averaged.
 */
import {
  computeLcoeUsdPerMwh,
  computeNpvUsd,
  computeProjectIrrPct,
  recompute,
} from './projectFinance';
import { computePhasedCashflows } from './projectFinancePhased';
import { canonicalFinancialAssumptions } from './financialScenarios';
import { resolvePhasedEngineInputs } from './financialPhaseEngineAdapter';
import type {
  FinancialAssumptions,
  FinancialAssumptionsCore,
  FinancialField,
  FinancialPhase,
} from '../types/financialAssumptions';
import { sortFinancialPhases, financialPhaseIncrementMw } from '../types/financialAssumptions';

export type SensitivityMetric = 'lcoe' | 'irr' | 'npv';

/**
 * The set of variables that meaningfully drive LCOE / IRR / NPV. The
 * order here is intentional — it's the order the form renders the
 * tornado chart's labels alphabetically before the per-swing sort
 * scrambles it.
 */
export const SENSITIVITY_VARIABLES: FinancialField[] = [
  'capexTotalUsd',
  'capacityFactorPct',
  'fixedOmPerKwYearUsd',
  'variableOmPerMwhUsd',
  'fuelCostPerMwhUsd',
  'waccPct',
  'ppaPricePerMwhUsd',
  'projectLifeYears',
];

export interface SensitivityRow {
  field: FinancialField;
  /** Base-case value of the field (before perturbation). null when unset. */
  baseValue: number | null;
  /** Recomputed metric at -deltaPct%. null when the perturbed case is invalid. */
  metricLow: number | null;
  /** Recomputed metric at +deltaPct%. null when the perturbed case is invalid. */
  metricHigh: number | null;
  /** Base-case metric. null when inputs are insufficient. */
  metricBase: number | null;
  /** |high - low| — used to sort the tornado bars. null when either bound is null. */
  swingAbs: number | null;
  /**
   * Which bound(s) `clampField` pulled back to a physically-defensible limit,
   * so the row was NOT evaluated at the full ±`deltaPct`.
   *
   * WHY THE READER NEEDS THIS. A capacity factor of 90% perturbed +20% is 108%,
   * which cannot happen, so the clamp holds it at 100% — a +11% move against
   * every other driver's +20%. Two consequences follow, and both mislead
   * silently: the tornado's caption ("±20% per driver") is false for that row,
   * and the bars are SORTED BY SWING, so a clamped driver systematically
   * under-ranks — it looks less important than drivers that actually matter
   * less. The numbers themselves are correct; what needs saying is that this
   * one bar is not a like-for-like comparison.
   */
  clampedBound?: 'low' | 'high' | 'both';
}

export interface SensitivityResult {
  metric: SensitivityMetric;
  /** Symmetric perturbation in percent of base. e.g. 20 ⇒ ±20%. */
  deltaPct: number;
  /** One row per `SENSITIVITY_VARIABLES`, sorted by `swingAbs` desc. */
  rows: SensitivityRow[];
  /** Base-case value of the active metric (so the UI can label "center"). */
  metricBase: number | null;
}

/** Where a phase row's CAPEX basis came from (B10). */
export type PhaseCapexBasis =
  /** The phase carries its own `capexTotalUsd` — priced, authoritative. */
  | 'phase-priced'
  /** Unpriced phase: campus total × (this increment ÷ Σ increments). */
  | 'campus-allocated'
  /** Neither a phase figure nor a campus total to allocate from. */
  | 'none';

/** Per-phase tornado row (R-58) — `field` carries the phase label. */
export interface PhasedSensitivityRow {
  phaseId: string;
  phaseLabel: string;
  capacityMW: number | null;
  /** Capacity weight used in the LCOE blend (increment MW). */
  weightMw: number;
  /** The CAPEX this row was priced on (USD) — after B10 allocation. */
  capexBasisUsd: number | null;
  /** How `capexBasisUsd` was resolved. */
  capexBasis: PhaseCapexBasis;
  metricBase: number | null;
  metricLow: number | null;
  metricHigh: number | null;
  swingAbs: number | null;
}

/**
 * How the per-phase rows were rolled up into `blendedMetricBase` (B10).
 *
 * The three metrics do NOT roll up the same way, and treating them alike is
 * exactly the defect this type exists to prevent:
 *   - `energy-weighted` — LCOE is a per-MWh RATE, so it is averaged over the
 *     energy each phase contributes (never summed).
 *   - `sum`             — NPV is ADDITIVE (dollars), so it is summed.
 *   - `aggregate-vector`— IRR is a RATE with no additive or weighted-average
 *     roll-up; the only defensible project figure is the one solved on the
 *     SUMMED cashflow vector (`computePhasedCashflows().blendedIrrPct`).
 *   - `not-blendable`   — the aggregate vector could not be built, so no
 *     project-level figure is reported (rather than a meaningless average).
 */
export type PhasedBlendKind = 'energy-weighted' | 'sum' | 'aggregate-vector' | 'not-blendable';

export interface PhasedSensitivityResult extends SensitivityResult {
  /** One row per phase when `phases.length > 1`; empty for single-phase. */
  phaseRows: PhasedSensitivityRow[];
  /**
   * Project-level roll-up of the per-phase rows — metric-appropriate (see
   * `PhasedBlendKind`). Null when the metric cannot be rolled up from what is
   * available.
   *
   * NOTE this is NOT `metricBase`. `metricBase` (inherited from
   * `SensitivityResult`) stays the WHOLE-PROJECT base the tornado rows were
   * perturbed around, so the chart's centre line and its bars share one basis.
   */
  blendedMetricBase: number | null;
  blendKind: PhasedBlendKind;
  /** Human-readable basis for `blendedMetricBase` — render it beside the number. */
  blendBasis: string;
}

/**
 * Pick a metric value off a `recompute()` result. Centralised so
 * adding a new metric is a one-line change.
 */
function readMetric(metric: SensitivityMetric, core: FinancialAssumptionsCore): number | null {
  switch (metric) {
    case 'lcoe': return computeLcoeUsdPerMwh(core);
    case 'irr':  return computeProjectIrrPct(core);
    case 'npv':  return computeNpvUsd(core);
  }
}

/**
 * Build a tornado-chart sensitivity table for the given core
 * assumptions + metric. `deltaPct` defaults to 20 (i.e. ±20%).
 *
 * Edge cases (return row with null metricLow/High/swingAbs):
 *   - Field is unset (null) on the base case — no defensible perturbation.
 *   - Field is non-numeric (capacityMW, projectLifeYears are years not
 *     percentages but we still scale ±deltaPct on them).
 *   - Perturbed value would drive the metric to NaN / -Infinity
 *     (e.g. capacityFactorPct > 100).
 */
export function computeSensitivity(
  base: FinancialAssumptionsCore,
  metric: SensitivityMetric = 'lcoe',
  deltaPct: number = 20,
): SensitivityResult {
  // First make sure WACC + capex/kW are populated for the base case so
  // the tornado isn't constantly hitting "metric=null" on its center.
  const recomputed = recompute(base);
  const effectiveBase: FinancialAssumptionsCore = {
    ...base,
    waccPct: base.waccPct ?? recomputed.waccPct,
    capexPerKwUsd: base.capexPerKwUsd ?? recomputed.capexPerKwUsd,
  };
  const metricBase = readMetric(metric, effectiveBase);

  const factor = deltaPct / 100;
  const rows: SensitivityRow[] = SENSITIVITY_VARIABLES.map((field) => {
    const baseValue = readField(base, field);
    if (baseValue == null || !Number.isFinite(baseValue) || baseValue === 0) {
      return {
        field,
        baseValue,
        metricLow: null,
        metricHigh: null,
        metricBase,
        swingAbs: null,
      };
    }
    const intendedLow = baseValue * (1 - factor);
    const intendedHigh = baseValue * (1 + factor);
    const lowValue = clampField(field, intendedLow);
    const highValue = clampField(field, intendedHigh);
    // Float-tolerant: only a REAL clamp counts, not arithmetic noise.
    const lowClamped = Math.abs(lowValue - intendedLow) > Math.abs(intendedLow) * 1e-9;
    const highClamped = Math.abs(highValue - intendedHigh) > Math.abs(intendedHigh) * 1e-9;
    const clampedBound = lowClamped && highClamped
      ? 'both' as const
      : lowClamped ? 'low' as const : highClamped ? 'high' as const : undefined;
    const metricLow = readMetric(metric, applyField(effectiveBase, field, lowValue));
    const metricHigh = readMetric(metric, applyField(effectiveBase, field, highValue));
    const swingAbs = metricLow != null && metricHigh != null
      ? Math.abs(metricHigh - metricLow)
      : null;
    return {
      field, baseValue, metricLow, metricHigh, metricBase, swingAbs,
      ...(clampedBound ? { clampedBound } : {}),
    };
  });

  rows.sort((a, b) => {
    const av = a.swingAbs ?? -1;
    const bv = b.swingAbs ?? -1;
    return bv - av;
  });

  return { metric, deltaPct, rows, metricBase };
}

/**
 * B10 — CAPEX basis for ONE phase.
 *
 * DECISION: an UNPRICED phase is ALLOCATED `campusCapexUsd × (increment ÷ Σ
 * increments)`, not nulled and never the whole campus total.
 *
 * WHY the allocation and not a null row: this is the same capacity-fraction
 * rule the phased kernel already applies —
 * `projectFinancePhased.resolvePhaseCapexUsd` uses the authoritative per-phase
 * figure when there is one and otherwise splits the project total by
 * `phaseMw / totalCapacityMW` — so the tornado and the report price the same
 * plant. Under the cumulative convention Σ increments == the final cumulative
 * capacity == the kernel's `totalCapacityMW`, so the fractions sum to 1.0 and
 * the split neither creates nor destroys CAPEX. It also keeps the panel usable
 * for the common case (campus CAPEX entered once, phases carrying only
 * capacity + COD), where nulling every row would blank the whole per-phase
 * table.
 *
 * WHAT IT FIXES: `phase.capexTotalUsd ?? campus.capexTotalUsd` handed an
 * unpriced phase the WHOLE campus CAPEX and then measured it against that
 * phase's INCREMENT MW — pricing a 300 MW first phase as if it cost the entire
 * 1,750 MW programme (measured: per-phase LCOE $147.35/MWh against a campus
 * $86.06/MWh for the same project).
 *
 * RESIDUAL CAVEAT (B15, not fixed here): when the campus `capexTotalUsd` field
 * itself holds only an early phase's spend, the allocation inherits that
 * understatement. The cure is a per-phase CAPEX figure (or the BoM-priced
 * basis the report uses), not a different split.
 */
function resolvePhaseCapexBasis(
  campusCapexTotalUsd: number | null,
  phaseCapexTotalUsd: number | null,
  incrementMw: number | null,
  totalIncrementMw: number,
): { capexUsd: number | null; basis: PhaseCapexBasis } {
  if (
    phaseCapexTotalUsd != null
    && Number.isFinite(phaseCapexTotalUsd)
    && phaseCapexTotalUsd > 0
  ) {
    return { capexUsd: phaseCapexTotalUsd, basis: 'phase-priced' };
  }
  if (
    campusCapexTotalUsd == null
    || !Number.isFinite(campusCapexTotalUsd)
    || incrementMw == null
    || !Number.isFinite(incrementMw)
    || incrementMw <= 0
    || !(totalIncrementMw > 0)
  ) {
    return { capexUsd: null, basis: 'none' };
  }
  return {
    capexUsd: campusCapexTotalUsd * (incrementMw / totalIncrementMw),
    basis: 'campus-allocated',
  };
}

/** Σ of the phases' capacity INCREMENTS (MW) — the allocation denominator. */
function totalIncrementMw(phases: FinancialPhase[]): number {
  let total = 0;
  for (let i = 0; i < phases.length; i += 1) {
    const inc = financialPhaseIncrementMw(phases, i);
    if (inc != null && Number.isFinite(inc) && inc > 0) total += inc;
  }
  return total;
}

function coreForPhase(
  campus: FinancialAssumptionsCore,
  phase: FinancialPhase,
  incrementMw: number | null,
  capexUsd: number | null,
  isFirstPhase: boolean,
): FinancialAssumptionsCore {
  return {
    ...campus,
    // LAND is bought ONCE, up front — the site is not re-bought each phase.
    // `computeEffectiveCapexUsd` adds `landCost.totalUsd` to whatever core it
    // is handed, so it has to be stripped from every phase but the first, the
    // same rule `projectFinancePhased.resolveEffectivePhaseCapexUsd` applies.
    landCost: isFirstPhase ? campus.landCost : undefined,
    // CUMULATIVE convention: the per-phase economics (LCOE/energy) are sized
    // by the MW ADDED this phase (the increment), NOT the cumulative-at-phase
    // figure stored on `phase.capacityMW` — late phases would otherwise
    // over-state capacity and under-state $/MWh.
    capacityMW: incrementMw ?? phase.capacityMW ?? campus.capacityMW,
    capexTotalUsd: capexUsd,
    // Never inherit the CAMPUS $/kW onto a phase core: it is campus CAPEX over
    // campus MW, a different basis from this phase's. `recompute()` derives the
    // phase's own from `capexTotalUsd ÷ increment MW`.
    capexPerKwUsd: phase.capexPerKwUsd ?? null,
    capacityFactorPct: phase.capacityFactorPct ?? campus.capacityFactorPct,
    inServiceDate: phase.inServiceDate ?? campus.inServiceDate,
    constructionMonths: phase.constructionMonths ?? campus.constructionMonths,
    fixedOmPerKwYearUsd: phase.fixedOmPerKwYearUsd ?? campus.fixedOmPerKwYearUsd,
    variableOmPerMwhUsd: phase.variableOmPerMwhUsd ?? campus.variableOmPerMwhUsd,
    fuelCostPerMwhUsd: phase.fuelCostPerMwhUsd ?? campus.fuelCostPerMwhUsd,
    technology: phase.technology ?? campus.technology,
  };
}

/**
 * R-58 — per-phase sensitivity with a METRIC-APPROPRIATE project roll-up.
 * Falls back to `computeSensitivity` when only one phase is present.
 *
 * B10 fixes, all three of them:
 *
 *   1. The scenario. The campus core is resolved through
 *      `canonicalFinancialAssumptions(fa)` — core AND phases — so this path
 *      measures the SAME case the single-phase path does (`canonicalCore(fa)`,
 *      the core the form and the report read). It previously ran on the raw
 *      `fa`, so a canonical Upside/Downside scenario moved the single-phase
 *      tornado and left the phased one on base numbers.
 *   2. The CAPEX basis. See `resolvePhaseCapexBasis` — an unpriced phase is
 *      allocated its capacity share of the campus total, never handed the
 *      whole campus CAPEX against its own increment MW.
 *   3. The roll-up. LCOE is energy-weighted, NPV is SUMMED, and IRR is solved
 *      on the aggregate cashflow vector (or withheld) — never averaged. See
 *      `PhasedBlendKind`.
 */
export function computePhasedSensitivity(
  fa: FinancialAssumptions,
  metric: SensitivityMetric = 'lcoe',
  deltaPct: number = 20,
): PhasedSensitivityResult {
  // (1) CANONICAL SCENARIO — same resolution as the single-phase caller
  // (`canonicalCore(fa)`), extended to the phases so a scenario that scales
  // CAPEX reaches the per-phase figures too rather than only the campus core.
  const canonicalFa = canonicalFinancialAssumptions(fa);
  const campus: FinancialAssumptionsCore = { ...canonicalFa };
  const phases = sortFinancialPhases(canonicalFa.phases ?? []);
  const baseResult = computeSensitivity(campus, metric, deltaPct);
  if (phases.length <= 1) {
    return {
      ...baseResult,
      phaseRows: [],
      blendedMetricBase: baseResult.metricBase,
      blendKind: 'sum',
      blendBasis: 'Single-phase project — the project figure is the phase figure.',
    };
  }

  const factor = deltaPct / 100;
  const incrementTotalMw = totalIncrementMw(phases);
  const phaseRows: PhasedSensitivityRow[] = phases.map((phase, i) => {
    // Energy/CAPEX basis = MW added this phase (increment), not cumulative.
    const incrementMw = financialPhaseIncrementMw(phases, i);
    const { capexUsd, basis } = resolvePhaseCapexBasis(
      campus.capexTotalUsd ?? null,
      phase.capexTotalUsd ?? null,
      incrementMw,
      incrementTotalMw,
    );
    const phaseCore = coreForPhase(campus, phase, incrementMw, capexUsd, i === 0);
    const effective = recompute(phaseCore);
    const core: FinancialAssumptionsCore = {
      ...phaseCore,
      waccPct: campus.waccPct ?? effective.waccPct,
      capexPerKwUsd: phase.capexPerKwUsd ?? effective.capexPerKwUsd,
    };
    const metricBase = readMetric(metric, core);
    // Blend weight = the phase's INCREMENTAL MW (energy contributed by its
    // NEW plant); cumulative would double-weight late phases.
    const weightMw = incrementMw ?? phase.capacityMW ?? 0;
    const row = {
      phaseId: phase.phaseId,
      phaseLabel: phase.label ?? `Phase ${phase.sequence}`,
      capacityMW: phase.capacityMW,
      weightMw,
      capexBasisUsd: capexUsd,
      capexBasis: basis,
      metricBase,
    };
    if (capexUsd == null || !Number.isFinite(capexUsd) || capexUsd === 0) {
      return { ...row, metricLow: null, metricHigh: null, swingAbs: null };
    }
    const metricLow = readMetric(metric, { ...core, capexTotalUsd: capexUsd * (1 - factor) });
    const metricHigh = readMetric(metric, { ...core, capexTotalUsd: capexUsd * (1 + factor) });
    const swingAbs = metricLow != null && metricHigh != null
      ? Math.abs(metricHigh - metricLow)
      : null;
    return { ...row, metricLow, metricHigh, swingAbs };
  });

  const { blendedMetricBase, blendKind, blendBasis } = blendPhaseMetric(
    metric,
    phaseRows,
    canonicalFa,
    campus,
  );

  return {
    ...baseResult,
    // `metricBase` deliberately stays the WHOLE-PROJECT base that
    // `computeSensitivity` perturbed the driver rows around. Overwriting it
    // with the phase roll-up put the tornado's centre line on one basis and its
    // bars on another, and made the panel label a phase-average "current NPV
    // base". The roll-up is reported separately, with its basis.
    phaseRows,
    blendedMetricBase,
    blendKind,
    blendBasis,
  };
}

/**
 * B10 — roll per-phase base metrics up to ONE project figure, correctly for
 * the metric in question. Averaging is only ever valid for the RATE that has a
 * natural weight (LCOE, per MWh); dollars are summed; IRR is re-solved.
 */
function blendPhaseMetric(
  metric: SensitivityMetric,
  phaseRows: PhasedSensitivityRow[],
  canonicalFa: FinancialAssumptions,
  campus: FinancialAssumptionsCore,
): { blendedMetricBase: number | null; blendKind: PhasedBlendKind; blendBasis: string } {
  if (metric === 'lcoe') {
    // LCOE is a $/MWh RATE — weight it by the energy each phase contributes.
    // Weight = the phase's increment MW, a first-order energy proxy (capacity
    // factor and phase life are held at the campus values here). The report
    // kernel's `computeBlendedLcoe` weights MW·yr, which additionally prices
    // the shorter operating life of a late phase.
    const usable = phaseRows.filter(r => r.weightMw > 0 && r.metricBase != null);
    const totalW = usable.reduce((s, r) => s + r.weightMw, 0);
    if (totalW <= 0) {
      return {
        blendedMetricBase: null,
        blendKind: 'not-blendable',
        blendBasis: 'No phase carries both a capacity increment and a computable LCOE.',
      };
    }
    const blended = usable.reduce((s, r) => s + (r.metricBase as number) * r.weightMw, 0) / totalW;
    return {
      blendedMetricBase: blended,
      blendKind: 'energy-weighted',
      blendBasis: `Increment-MW-weighted across ${usable.length} phases (Σ ${totalW.toLocaleString('en-US')} MW) — LCOE is a per-MWh rate, so it is weighted, never summed.`,
    };
  }

  if (metric === 'npv') {
    // NPV is ADDITIVE — the project NPV is the SUM of the phases' discounted
    // flows. Averaging them reported roughly half the project value on a
    // two-phase build.
    const usable = phaseRows.filter(r => r.metricBase != null);
    if (usable.length === 0) {
      return {
        blendedMetricBase: null,
        blendKind: 'not-blendable',
        blendBasis: 'No phase has a computable NPV.',
      };
    }
    const summed = usable.reduce((s, r) => s + (r.metricBase as number), 0);
    return {
      blendedMetricBase: summed,
      blendKind: 'sum',
      blendBasis: `Σ of ${usable.length} phase NPVs — NPV is additive. Each phase is discounted to its OWN in-service year, so the sum is not calendar-aligned; the report's calendar-aligned project NPV comes from the phased kernel.`,
    };
  }

  // IRR is a RATE on a cashflow VECTOR. There is no weighted average of IRRs:
  // the project IRR must be solved on the SUMMED flows. Take it from the
  // kernel (`computePhasedCashflows`) rather than reimplementing the solver;
  // when the kernel cannot build the vector, report nothing.
  const inputs = resolvePhasedEngineInputs(canonicalFa.phases, null);
  const phased = inputs ? computePhasedCashflows(inputs.intent, campus, inputs.source) : null;
  if (phased?.blendedIrrPct != null && Number.isFinite(phased.blendedIrrPct)) {
    return {
      blendedMetricBase: phased.blendedIrrPct,
      blendKind: 'aggregate-vector',
      blendBasis: 'Project IRR solved on the AGGREGATE phased cashflow vector — per-phase IRRs cannot be averaged.',
    };
  }
  return {
    blendedMetricBase: null,
    blendKind: 'not-blendable',
    blendBasis: 'IRR is a rate — it cannot be averaged across phases, and the aggregate cashflow vector could not be built (needs per-phase capacity, CODs, PPA price, O&M and project life). Per-phase IRRs are shown individually.',
  };
}

/** Read a numeric field off a core. Returns null for non-numeric / null fields. */
function readField(core: FinancialAssumptionsCore, field: FinancialField): number | null {
  const v = (core as unknown as Record<string, unknown>)[field];
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  return v;
}

/** Write a numeric field on a shallow clone. */
function applyField(
  core: FinancialAssumptionsCore,
  field: FinancialField,
  value: number,
): FinancialAssumptionsCore {
  return { ...core, [field]: value };
}

/**
 * Clamp perturbed values into physically-defensible bands so the
 * tornado doesn't trip over an unreal capacity factor (105%) or
 * negative project life (when the base is small and the delta swings
 * past zero).
 */
function clampField(field: FinancialField, value: number): number {
  switch (field) {
    case 'capacityFactorPct':       return Math.max(1, Math.min(100, value));
    case 'projectLifeYears':        return Math.max(1, value);
    case 'capacityMW':              return Math.max(0.1, value);
    case 'capexTotalUsd':
    case 'capexPerKwUsd':
    case 'decommissioningUsd':
    case 'workingCapitalUsd':
    case 'fixedOmPerKwYearUsd':
    case 'variableOmPerMwhUsd':
    case 'fuelCostPerMwhUsd':
    case 'ppaPricePerMwhUsd':       return Math.max(0, value);
    case 'waccPct':                 return Math.max(0.5, Math.min(40, value));
    case 'debtRatePct':
    case 'equityHurdlePct':         return Math.max(0, Math.min(40, value));
    case 'debtToEquityRatio':       return Math.max(0, Math.min(1, value));
    case 'taxRatePct':
    case 'itcPct':
    case 'contingencyPct':
    case 'insuranceAndPropertyTaxPct':
    case 'ppaEscalatorPctPerYear':  return Math.max(0, Math.min(100, value));
    default:                        return value;
  }
}

/**
 * Human-readable label for the field. Mirrors the form's labels so the
 * tornado strip + sensitivity sliders share the same vocabulary.
 */
export function fieldLabel(field: FinancialField): string {
  switch (field) {
    case 'capexTotalUsd':           return 'Total CAPEX';
    case 'capacityFactorPct':       return 'Capacity factor';
    case 'fixedOmPerKwYearUsd':     return 'Fixed O&M';
    case 'variableOmPerMwhUsd':     return 'Variable O&M';
    case 'fuelCostPerMwhUsd':       return 'Fuel cost';
    case 'waccPct':                 return 'WACC';
    case 'ppaPricePerMwhUsd':       return 'PPA price';
    case 'projectLifeYears':        return 'Project life';
    case 'capacityMW':              return 'Capacity';
    case 'debtToEquityRatio':       return 'Debt fraction';
    case 'debtRatePct':             return 'Debt rate';
    case 'equityHurdlePct':         return 'Equity hurdle';
    case 'taxRatePct':              return 'Tax rate';
    case 'itcPct':                  return 'ITC';
    case 'ppaEscalatorPctPerYear':  return 'PPA escalator';
    case 'ppaTermYears':            return 'PPA term';
    default:                        return field;
  }
}

/** Format the metric value with the right precision + unit. */
export function formatMetric(metric: SensitivityMetric, value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  switch (metric) {
    case 'lcoe': return `$${value.toFixed(2)}/MWh`;
    case 'irr':  return `${value.toFixed(2)}%`;
    case 'npv': {
      const sign = value >= 0 ? '+' : '−';
      const abs = Math.abs(value);
      if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
      if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}k`;
      return `${sign}$${abs.toFixed(0)}`;
    }
  }
}
